'use client';

import { useCallback, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigation } from 'react-router';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from '@/hooks/use-navigate';
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { EatalyPlpSubcategoryTagButton } from '@/components/eataly-plp-subcategory-tag';
import EatalyPlpFiltersPanel from '@/components/eataly-plp-filters/plp-filters-panel';
import type { SiblingFolderLink } from '@/lib/api/content-route-index.server';
import { usePlpRefinements } from '@/components/eataly-plp-filters/hooks/use-plp-refinements';
import type { UsePlpRefinementsParams } from '@/components/eataly-plp-filters/lib/types';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/spinner';
import { searchWithContentFolderFilter, selectedContentFolderFilterIds } from '@/lib/content-folder-filter';

const LG_UP = '(min-width: 1024px)';

export interface EatalyCategoryPlpFiltersDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    result: UsePlpRefinementsParams['result'];
    refine?: string[];
    excludeRefinementAttributeIds?: readonly string[];
    categoryId?: string;
    showCategoryFilter?: boolean;
    /** i18n key for the footer count. Defaults to product PLP copy. */
    countLabelKey?: 'category:banner.productsAvailable' | 'categoryPlp:contentFolderListing.itemsAvailable';
    /** Editorial listings: child folders toggled as filters on the current listing. */
    folderLinks?: SiblingFolderLink[];
    trigger?: ReactElement;
}

export default function EatalyCategoryPlpFiltersDrawer({
    open,
    onOpenChange,
    result,
    refine = [],
    excludeRefinementAttributeIds,
    categoryId,
    showCategoryFilter = false,
    countLabelKey = 'category:banner.productsAvailable',
    folderLinks,
    trigger,
}: EatalyCategoryPlpFiltersDrawerProps): ReactElement {
    const { t } = useTranslation(['categoryPlp', 'category']);
    const location = useLocation();
    const navigation = useNavigation();
    const navigate = useNavigate();
    const isLgUp = useMediaQuery(LG_UP);
    const plp = usePlpRefinements({
        result,
        refine,
        excludeRefinementAttributeIds,
        categoryId,
        showCategoryFilter,
    });

    const isFolderLinks = folderLinks !== undefined;
    const isPending = plp.isPending;
    const listingSearch =
        navigation.location && navigation.location.pathname === location.pathname
            ? navigation.location.search
            : location.search;
    const selectedFolderIds = new Set(
        isFolderLinks
            ? selectedContentFolderFilterIds(
                  new URLSearchParams(listingSearch).getAll('refine'),
                  new Set(folderLinks.map((link) => link.id))
              )
            : []
    );

    const handleToggleFolderFilter = useCallback(
        (folderId: string) => {
            void navigate(
                {
                    pathname: location.pathname,
                    search: searchWithContentFolderFilter(location.search, folderId),
                },
                { preventScrollReset: true }
            );
        },
        [location.pathname, location.search, navigate]
    );

    const displayCount = result.total ?? 0;
    const countLabel = isPending ? (
        <Spinner size="sm" />
    ) : countLabelKey === 'categoryPlp:contentFolderListing.itemsAvailable' ? (
        t('categoryPlp:contentFolderListing.itemsAvailable', { count: displayCount })
    ) : (
        t('category:banner.productsAvailable', { count: displayCount })
    );

    const direction = isLgUp ? 'right' : 'bottom';

    const handleClearFilters = () => {
        plp.clearAllRefinements();
    };

    const handleShowProducts = () => {
        onOpenChange(false);
    };

    return (
        <Drawer autoFocus direction={direction} open={open} onOpenChange={onOpenChange} shouldScaleBackground={false}>
            {trigger ? <DrawerTrigger asChild>{trigger}</DrawerTrigger> : null}
            <DrawerContent
                className={cn(
                    'bg-neutral-white border-sand-95',
                    'data-[vaul-drawer-direction=bottom]:max-h-[90vh]',
                    'data-[vaul-drawer-direction=right]:mt-0 data-[vaul-drawer-direction=right]:max-h-dvh data-[vaul-drawer-direction=right]:h-dvh',

                    'lg:data-[vaul-drawer-direction=right]:w-[calc((100vw-52px)/3+16px)]',
                    'lg:data-[vaul-drawer-direction=right]:max-w-[calc((100vw-52px)/3+16px)]',
                    'lg:data-[vaul-drawer-direction=right]:sm:max-w-[calc((100vw-52px)/3+16px)]'
                )}>
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                    <div className="flex shrink-0 items-start justify-between gap-4 p-6">
                        <DrawerTitle className="heading-title-l">{t('categoryPlp:filtersDrawer.title')}</DrawerTitle>
                        <DrawerClose asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="size-10 shrink-0 rounded-full border border-transparent hover:border-neutral-80"
                                aria-label={t('categoryPlp:filtersDrawer.closeAriaLabel')}>
                                <X className="size-6 text-neutral-10" aria-hidden />
                            </Button>
                        </DrawerClose>
                    </div>

                    <DrawerDescription className="sr-only">
                        {isFolderLinks
                            ? t('categoryPlp:contentFolderListing.filtersDescription')
                            : t('categoryPlp:filtersDrawer.description')}
                    </DrawerDescription>

                    <div className="min-h-0 flex-1 overflow-y-auto pb-4 pt-6">
                        {isFolderLinks ? (
                            <Accordion type="single" collapsible defaultValue="categories" className="w-full px-6">
                                <AccordionItem value="categories" className="border-neutral-80">
                                    <AccordionTrigger className="body-copy-m font-bold text-neutral-10">
                                        {t('categoryPlp:contentFolderListing.categoriesFilterLabel')}
                                    </AccordionTrigger>
                                    <AccordionContent className="pt-0 pb-4">
                                        <div
                                            className={cn(
                                                'flex flex-wrap gap-2',
                                                isPending && 'pointer-events-none opacity-50'
                                            )}>
                                            {folderLinks.map((link) => {
                                                const isSelected = selectedFolderIds.has(link.id);
                                                return (
                                                    <EatalyPlpSubcategoryTagButton
                                                        key={link.id}
                                                        label={link.title}
                                                        isSelected={isSelected}
                                                        aria-pressed={isSelected}
                                                        onPress={() => handleToggleFolderFilter(link.id)}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </AccordionContent>
                                </AccordionItem>
                            </Accordion>
                        ) : (
                            <EatalyPlpFiltersPanel plp={plp} />
                        )}
                    </div>

                    <DrawerFooter className="shrink-0 border-t border-neutral-80 bg-neutral-white px-6 pb-6 pt-4">
                        <div className="flex w-full gap-2">
                            <Button
                                type="button"
                                hierarchy="secondary"
                                mode="light"
                                size="large"
                                className="flex-1 uppercase cursor-pointer"
                                onClick={handleClearFilters}
                                disabled={isPending}>
                                {t('categoryPlp:filtersDrawer.clearFilters')}
                            </Button>
                            <Button
                                type="button"
                                hierarchy="primary"
                                mode="light"
                                size="large"
                                className="flex-1 uppercase cursor-pointer"
                                onClick={handleShowProducts}
                                disabled={isPending}>
                                {countLabel}
                            </Button>
                        </div>
                    </DrawerFooter>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
